import { useEffect, useMemo, useRef, useState } from "react";
import { SessionGuardrailResult } from "@financial-advisory/data-access/api-types";
import { Dialog } from "@headlessui/react";
import { ReactFlowProvider } from "@xyflow/react";
import { ChevronDown, ShieldAlert, ShieldCheck } from "lucide-react";
import {
  LayoutFlowStatic,
  QuestionAnswer,
} from "@unlikelyai-magic/shared/components";
import {
  DecisionTree,
  Outcome,
  PaneType,
} from "@unlikelyai-magic/shared/data-access/api";
import { Modal } from "@unlikelyai-magic/ui/modals";
import { screenBreakpoints } from "@unlikelyai-magic/ui/variables";
import { cn } from "@unlikelyai-magic/utils";
import { FadingScrollContainer } from "./FadingScrollContainer";
import { guardrailPassed } from "./guardrailUtils";
import { MarkdownContent } from "./MarkdownContent";

const iterationFailed = (iteration: SessionGuardrailResult) =>
  iteration.guardrail_trees?.some((tree) => !guardrailPassed(tree));

const mapQuestionAnswers = (
  guardrailTree: SessionGuardrailResult["guardrail_trees"][number]
): QuestionAnswer[] =>
  guardrailTree.leaf_nodes?.map((node) => ({
    question: node.question,
    value: node.value === Outcome.TRUE ? Outcome.TRUE : Outcome.FALSE,
    reasoning: node.reasoning,
  })) ?? [];

export const GuardrailTimelineModal = ({
  iterations,
  onClose,
  showTimeline = true,
  title = "Guardrail Timeline",
  isInput = false,
}: {
  iterations: SessionGuardrailResult[];
  onClose: () => void;
  showTimeline?: boolean;
  title?: string;
  isInput?: boolean;
}) => {
  const sortedIterations = useMemo(
    () => [...iterations].sort((a, b) => a.sequence_number - b.sequence_number),
    [iterations]
  );

  const [selectedId, setSelectedId] = useState<string | null>(
    sortedIterations[0]?.id ?? null
  );
  const [selectedTreeId, setSelectedTreeId] = useState<string | null>(() => {
    const initial = sortedIterations[0];
    if (initial?.guardrail_trees?.length === 1) {
      return initial.guardrail_trees[0].id;
    }
    return null;
  });
  const accordionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const listRef = useRef<HTMLDivElement | null>(null);

  const selected = useMemo(
    () => sortedIterations.find((item) => item.id === selectedId) ?? null,
    [sortedIterations, selectedId]
  );

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [isMessageExpanded, setIsMessageExpanded] = useState(true);

  const isSingleTree = selected?.guardrail_trees?.length === 1;

  // Reset tree selection and message expansion when the user picks a
  // different attempt.  Handled in the click handler (not an effect) so
  // that unrelated `iterations` prop updates don't collapse the
  // currently-open graph or re-expand the message mid-interaction.
  const handleSelectIteration = (id: string) => {
    if (id === selectedId) return;
    setSelectedId(id);
    const iter = sortedIterations.find((item) => item.id === id);
    if (iter?.guardrail_trees?.length === 1) {
      setSelectedTreeId(iter.guardrail_trees[0].id);
    } else {
      setSelectedTreeId(null);
    }
    setIsMessageExpanded(true);
  };

  return (
    <Dialog open={true} onClose={onClose}>
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center">
        <Modal className="w-[60vw] h-[80vh] flex flex-col">
          <Modal.Header title={title} onClose={onClose} />
          <Modal.Content className="flex-1 min-h-0 overflow-hidden">
            <div className="flex h-full flex-col gap-3 bg-white min-h-0">
              {showTimeline && (
                <div className="flex-none">
                  <div className="flex items-center justify-center gap-3">
                    {sortedIterations.map((iteration, idx) => {
                      const failed = iterationFailed(iteration);
                      const isSelected = iteration.id === selected?.id;
                      return (
                        <div
                          key={iteration.id}
                          className="flex items-center gap-3"
                        >
                          <button
                            onClick={() => handleSelectIteration(iteration.id)}
                            className={cn(
                              "flex items-center gap-2 rounded-full border transition-colors",
                              isSelected
                                ? failed
                                  ? "border-red-500 bg-red-500"
                                  : "border-green-600 bg-green-600"
                                : "border-slate-200 bg-white hover:bg-slate-50",
                              sortedIterations.length <= 4 &&
                                windowWidth > parseInt(screenBreakpoints.xl)
                                ? "px-4 py-2"
                                : "px-3 py-3"
                            )}
                          >
                            {failed ? (
                              <ShieldAlert
                                className={cn(
                                  "h-6 w-6",
                                  isSelected ? "text-white" : "text-red-500"
                                )}
                              />
                            ) : (
                              <ShieldCheck
                                className={cn(
                                  "h-6 w-6",
                                  isSelected ? "text-white" : "text-green-600"
                                )}
                              />
                            )}
                            {sortedIterations.length <= 4 &&
                              windowWidth > parseInt(screenBreakpoints.xl) && (
                                <span
                                  className={cn(
                                    "text-sm font-semibold",
                                    isSelected ? "text-white" : "text-slate-700"
                                  )}
                                >
                                  Attempt {iteration.sequence_number}
                                </span>
                              )}
                          </button>
                          {idx < sortedIterations.length - 1 && (
                            <span className="text-slate-300 text-xl leading-none select-none">
                              ⋯
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex flex-col flex-1 min-h-0 rounded-md border border-slate-200 bg-white">
                {selected ? (
                  <div className="flex h-full flex-col">
                    <div className="border-b border-slate-200 flex-none">
                      <button
                        type="button"
                        onClick={() => setIsMessageExpanded(!isMessageExpanded)}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
                            {iterationFailed(selected) ? (
                              <ShieldAlert className="h-6 w-6 text-red-500" />
                            ) : (
                              <ShieldCheck className="h-6 w-6 text-green-600" />
                            )}
                          </div>
                          {isMessageExpanded ? (
                            <div className="flex-1 min-w-0 space-y-1">
                              <FadingScrollContainer
                                className={cn(
                                  selected.guardrail_trees?.length
                                    ? "max-h-64"
                                    : "max-h-128"
                                )}
                              >
                                <p className="text-sm font-semibold text-slate-700 pb-2">
                                  Attempt {selected.sequence_number}
                                </p>
                                <div className="text-sm leading-relaxed text-slate-700">
                                  <MarkdownContent
                                    content={
                                      selected.message?.content ??
                                      "No assistant message recorded"
                                    }
                                  />
                                </div>
                              </FadingScrollContainer>
                            </div>
                          ) : (
                            <p className="flex-1 min-w-0 text-sm text-slate-400">
                              Attempt {selected.sequence_number}
                            </p>
                          )}
                          <ChevronDown
                            className={cn(
                              "h-4 w-4 text-slate-400 flex-shrink-0 transition-transform duration-200",
                              isMessageExpanded ? "rotate-180" : ""
                            )}
                          />
                        </div>
                      </button>
                    </div>

                    <div
                      ref={listRef}
                      className={cn(
                        "flex-1 min-h-0 px-4 py-3",
                        isSingleTree
                          ? "flex flex-col"
                          : selected.guardrail_trees?.length
                            ? "overflow-y-auto space-y-3 scroll-pt-3"
                            : "flex items-center justify-center"
                      )}
                    >
                      {selected.guardrail_trees?.length ? (
                        selected.guardrail_trees
                          .slice()
                          .sort((a, b) => {
                            const aFailed = !guardrailPassed(a);
                            const bFailed = !guardrailPassed(b);
                            if (aFailed === bFailed) return 0;
                            return aFailed ? -1 : 1;
                          })
                          .map((tree) => {
                            const treeFailed = !guardrailPassed(tree);
                            const isOpen = selectedTreeId === tree.id;
                            const handleToggle = () => {
                              const next = isOpen ? null : tree.id;
                              setSelectedTreeId(next);
                              if (!isOpen) {
                                setTimeout(() => {
                                  accordionRefs.current[
                                    tree.id
                                  ]?.scrollIntoView({
                                    behavior: "smooth",
                                    block: "start",
                                  });
                                }, 350);
                              }
                            };
                            return (
                              <div
                                key={tree.id}
                                ref={(el) =>
                                  (accordionRefs.current[tree.id] = el)
                                }
                                className={cn(
                                  "rounded-md border border-slate-200 bg-white shadow-sm",
                                  isSingleTree && isOpen && "flex-1 flex flex-col min-h-0"
                                )}
                              >
                                <button
                                  type="button"
                                  onClick={handleToggle}
                                  className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-slate-50"
                                >
                                  {treeFailed ? (
                                    <ShieldAlert className="h-5 w-5 text-red-500" />
                                  ) : (
                                    <ShieldCheck className="h-5 w-5 text-green-600" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm font-semibold text-slate-700">
                                      {tree.guardrail_tree.name}
                                    </span>
                                    <p className="text-xs text-slate-500 mt-0.5">
                                      {isInput
                                        ? treeFailed
                                          ? `The user message did trigger the ${tree.guardrail_tree.name} input guardrail.`
                                          : `The user message did not trigger the ${tree.guardrail_tree.name} input guardrail.`
                                        : treeFailed
                                          ? `The agent message did trigger the ${tree.guardrail_tree.name} guardrail.`
                                          : `The agent message did not trigger the ${tree.guardrail_tree.name} guardrail.`
                                      }
                                    </p>
                                  </div>
                                  <ChevronDown
                                    className={cn(
                                      "ml-auto h-3.5 w-3.5 text-slate-500 transition-transform flex-shrink-0",
                                      isOpen ? "rotate-180" : ""
                                    )}
                                  />
                                </button>
                                <div
                                  className={cn(
                                    "overflow-hidden",
                                    isSingleTree
                                      ? isOpen
                                        ? "flex-1 min-h-0 flex flex-col"
                                        : "max-h-0"
                                      : cn(
                                          "transition-[max-height] duration-300 ease-in-out",
                                          isOpen
                                            ? isMessageExpanded
                                              ? "max-h-[35vh]"
                                              : "max-h-[50vh]"
                                            : "max-h-0"
                                        )
                                  )}
                                >
                                  {isOpen && tree.guardrail_tree?.template && (
                                    <div
                                      className={cn(
                                        "border-t border-slate-200 bg-slate-50 p-2",
                                        isSingleTree
                                          ? "flex-1 min-h-0"
                                          : isMessageExpanded
                                            ? "h-[35vh]"
                                            : "h-[50vh]"
                                      )}
                                    >
                                      <ReactFlowProvider>
                                        <LayoutFlowStatic
                                          panes={[
                                            {
                                              paneType: PaneType.DEFINITION,
                                              definition: {
                                                rootNode:
                                                  tree.guardrail_tree.template,
                                                name: tree.guardrail_tree.name,
                                              } as DecisionTree,
                                              id: tree.guardrail_tree.id,
                                              explanation:
                                                tree.guardrail_tree.name,
                                              excerpt: tree.guardrail_tree.name,
                                            },
                                          ]}
                                          questionAnswers={mapQuestionAnswers(
                                            tree
                                          )}
                                        />
                                      </ReactFlowProvider>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded-md px-4 py-3 text-sm text-slate-700 text-center">
                          <p>
                            <strong>This message is valid by design.</strong>
                            <br />
                            No guardrail graphs to display for this attempt.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-slate-500">
                    Select an attempt to view details
                  </div>
                )}
              </div>
            </div>
          </Modal.Content>
        </Modal>
      </div>
    </Dialog>
  );
};
